import { useState } from 'react';

import { useLocalStorage, useViewportSize } from '@mantine/hooks';

/** The width the page gives up to the agent until the person drags it wider. */
const DEFAULT_WIDTH = 420;

/** Narrower than this, a reply's lists and tables wrap too much to read. */
const MIN_WIDTH = 320;

/** The page keeps at least this much beside the panel. */
const PAGE_MIN_WIDTH = 360;

/** The arrow keys move the edge of the panel by this much. */
export const AGENT_PANEL_KEY_STEP = 16;

/**
 * A width the panel can take on a viewport: never under the minimum, and
 * never so wide that the page has no room left.
 */
function clampWidth(width: number, viewportWidth: number): number {
  return Math.round(
    Math.min(Math.max(width, MIN_WIDTH), Math.max(MIN_WIDTH, viewportWidth - PAGE_MIN_WIDTH)),
  );
}

export type AgentPanelWidth = {
  width: number;
  setWidth: (width: number) => void;
  /** While the person drags the edge, the page follows it with no transition. */
  resizing: boolean;
  setResizing: (resizing: boolean) => void;
};

/**
 * The width of the agent panel, which the person sets by dragging its edge.
 * It outlasts a reload, and it shrinks to fit when the window gets narrower.
 */
export function useAgentPanelWidth(): AgentPanelWidth {
  const { width: viewportWidth } = useViewportSize();
  const [stored, setStored] = useLocalStorage({
    key: 'teo.agent.width',
    defaultValue: DEFAULT_WIDTH,
    getInitialValueInEffect: false,
  });
  const [resizing, setResizing] = useState(false);
  // Before the first measure the viewport reads 0; the stored width stands until then.
  const width = viewportWidth === 0 ? stored : clampWidth(stored, viewportWidth);
  return {
    width,
    setWidth: (next) => setStored(clampWidth(next, viewportWidth || window.innerWidth)),
    resizing,
    setResizing,
  };
}

/**
 * The header and footer end where the panel starts. AppShell's default layout
 * runs them under the aside, and the panel here is full height.
 */
export const CLEAR_OF_AGENT_PANEL = { marginInlineEnd: 'var(--app-shell-aside-offset, 0rem)' };

export const __private = { clampWidth };
